<template>
  <!-- Design-system look: the headline of a sidebar section's page (a menu.js entry with a submenu:
       guests, content, event app, tickets …): the page's name with the breadcrumb trail beside it. The
       section's pages sit in their own column beside the content (SectionNav); on narrow screens,
       where that column does not fit, they show here as one row of pill tabs instead. Same entries
       and gates as the sidebar flyout it replaces; each page is its own route, so links, Back and
       reload keep working. The event app adds its preview buttons and locks its pages until it
       exists. From lg up the headline sits on the baseline of the event name in the section column's
       header (SectionNav); the 4px offset is relative only (no transform, so the preview popovers stay anchored), so the heights above the page stay as they are. -->
  <!-- `max-lg:h-auto`: the fixed title height is one line of breadcrumb. Narrow enough and the
       breadcrumb wraps (it is `flex-wrap`) and needs 58px of the 34px this reserves - and because
       the row is `overflow: visible`, the second line does not clip, it spills out under the tab
       strip below and is painted over. Measured on the guest list at 375px, where the "Gästeliste"
       crumb sat behind the "Gästeliste" pill. From lg up the height is unchanged. -->
  <div
    ref="head"
    data-breadcrumbs-row
    class="section-tabs-head mb-2.5 flex h-(--event-app-tabs-title) items-center justify-between gap-4 max-lg:h-auto lg:-mt-1.5 lg:mb-[21px] lg:relative lg:top-1"
  >
    <!-- One line: the row has a fixed height, so a long breadcrumb trail shortens ("…") instead of
         wrapping and pushing the headline out of view. Below lg there is no room left for a trail
         beside the title (it was squeezed to "A. / I / /"), and the pill row under it already says
         where the page is, so the trail is left out there. -->
    <div class="flex min-w-0 items-center gap-x-5 [&_ol]:flex-nowrap">
      <!-- Below lg the title shares its line with the bell and the user menu and may wrap to a
           second line; the heights above the page follow (headHeight below). -->
      <h1
        class="page-title min-w-0 lg:flex-none"
        data-test="section-title"
        v-text="$t(activeLabelKey)"
      />
      <!-- A page may put a control right after the title here (the planner's help (?)). -->
      <span
        id="planner-title-help"
        class="-ml-3 flex items-center empty:hidden"
      />
      <Breadcrumbs class="max-lg:hidden" />
    </div>

    <div class="flex flex-none items-center gap-4">
      <!-- The Published / Draft preview of the event app, for every tab (was one toolbar per page).
           Below md the two buttons do not fit beside the title and painted over it; the App Config
           dashboard has both previews with their links and QR codes. -->
      <div
        v-if="preview"
        class="flex flex-none items-center gap-1.5 max-md:hidden"
        data-test="event-app-preview"
      >
        <PreviewButton
          :label="$t('eap.settings.preview.published_label')"
          :url="preview.livePreviewUrl"
          :description="
            preview.isPublished
              ? $t('portal.editor.preview.scan_published')
              : $t('portal.editor.preview.publish_app_to_share')
          "
          :dot-color="preview.isPublished ? 'green' : 'gray'"
          :link-url="preview.isPublished ? null : preview.eventAppPagesUrl"
          :link-label="$t('portal.editor.preview.go_to_pages')"
        />
        <PreviewButton
          :label="$t('portal.editor.preview.draft_label')"
          :url="preview.draftPreviewUrl"
          :description="$t('portal.editor.preview.scan_draft')"
          :note="$t('portal.editor.preview.link_validity')"
          dot-color="amber"
        />
      </div>

      <HeaderActions />
    </div>
  </div>
  <nav
    class="mb-3.5 flex h-(--event-app-tabs-bar) items-center gap-1.5 overflow-x-auto [scrollbar-width:none] lg:hidden"
    :aria-label="$t(`common.${section.label}`)"
    data-test="section-tabs"
  >
    <template v-for="tab in tabs" :key="tab.name">
      <!-- Without an event app only Setup can be used; the other tabs show as unavailable. -->
      <span
        v-if="tab.locked"
        class="flex-none cursor-not-allowed rounded-(--ds-radius-pill) px-[15px] py-2 text-[13.5px] font-medium whitespace-nowrap text-(--ds-text-secondary) opacity-45"
        aria-disabled="true"
        :data-test="`section-tab-${tab.label}`"
      >
        {{ $t(tab.labelKey) }}
      </span>
      <Link
        v-else
        :href="tab.href"
        class="flex-none rounded-(--ds-radius-pill) px-[15px] py-2 text-[13.5px] whitespace-nowrap no-underline transition-colors duration-150"
        :class="
          tab.active
            ? 'bg-(--ds-surface-brand) font-semibold text-(--ds-text-brand)'
            : 'font-medium text-(--ds-text-secondary) hover:bg-(--ds-color-grey-50) hover:text-(--ds-text-primary)'
        "
        :aria-current="tab.active ? 'page' : undefined"
        :data-test="`section-tab-${tab.label}`"
      >
        {{ $t(tab.labelKey) }}
      </Link>
    </template>
    <!-- „So funktioniert's“ ends the planner's pills (Arnold, 02.11.2026, UX1). -->
    <PlannerVideoNavRow
      v-if="section.label === 'planner' && plannerVideoAvailable"
      variant="pill"
    />
  </nav>

  <!-- A tab switch waits for the server before the page changes; cover the old page meanwhile.
       Planner pages get a skeleton of the coming page instead (C5, PlannerPageSkeleton). -->
  <EmbedLoading
    v-if="
      pendingSectionPage !== null && !isPlannerComponent(pendingSectionPage)
    "
    class="absolute inset-x-0 bottom-0 top-[var(--event-app-tabs-offset)] z-10"
    data-test="section-tabs-loading"
  />
</template>

<script setup>
import EmbedLoading from '@/Components/EventApp/EmbedLoading.vue'
import Breadcrumbs from '@/Components/Sidebar/Breadcrumbs.vue'
import HeaderActions from '@/Components/Sidebar/HeaderActions.vue'
import PreviewButton from '@/Components/EventApp/PreviewButton.vue'
import {
  pendingSectionPage,
  useCurrentSectionMenu,
  useSectionPages,
} from '@/composables/sectionTabs'
import { isPlannerComponent } from '@/composables/plannerSkeleton'
import { useStickyPageHead } from '@/composables/stickyPageHead'
import PlannerVideoNavRow from '@/Components/Planner/PlannerVideoNavRow.vue'
import { usePlannerVideoAvailable } from '@/composables/plannerVideo'
import { Link, router, usePage } from '@inertiajs/vue3'
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from 'vue'

const section = computed(
  () => useCurrentSectionMenu() ?? { label: '', child: [] },
)

const plannerVideoAvailable = usePlannerVideoAvailable()

const isEventApp = computed(() => section.value.name === 'EventApp')

const preview = computed(() =>
  isEventApp.value ? (usePage().props.eventAppTabsPreview ?? null) : null,
)

const tabs = useSectionPages()

/**
 * Below lg the headline's height depends on the title (one line or two) and the bell beside it,
 * while the toolkit screens below it are placed from a fixed height (theme.css,
 * --event-app-tabs-offset). So the real height is handed to those offsets: a two-line title can
 * not slide the Event Map under the pill row. From lg up the row has its fixed height anyway.
 */
const head = useTemplateRef('head')

useStickyPageHead(head)
let headObserver = null

onMounted(() => {
  const main = head.value?.closest('[data-event-app-tabs]')

  if (!main || typeof ResizeObserver === 'undefined') {
    return
  }

  headObserver = new ResizeObserver(([entry]) => {
    main.style.setProperty(
      '--event-app-tabs-head',
      `${Math.ceil(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height)}px`,
    )
  })
  headObserver.observe(head.value)
})

onBeforeUnmount(() => headObserver?.disconnect())

/** The headline names the page shown (or on its way), falling back to the section. */
const activeLabelKey = computed(
  () =>
    tabs.value.find((tab) => tab.active)?.labelKey ??
    `common.${section.value.label}`,
)

/**
 * Inertia visits to another page of the section (a tab, the section column, or anything else
 * navigating there) switch the active page at once and show the loading state until the new
 * page is in.
 */
const stopStartListener = router.on('start', (event) => {
  const target = new URL(event.detail.visit.url, window.location.origin)
    .pathname
  const tab = tabs.value.find(
    (candidate) =>
      new URL(candidate.href, window.location.origin).pathname === target,
  )
  if (tab !== undefined && tab.name !== usePage().component) {
    pendingSectionPage.value = tab.name
  }
})

const stopFinishListener = router.on('finish', () => {
  pendingSectionPage.value = null
})

onBeforeUnmount(() => {
  stopStartListener()
  stopFinishListener()
  pendingSectionPage.value = null
})
</script>
